import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { FIELD_NAV_MODULES } from "./navigationConfig";
import "../../assets/scss/components/_mobile-menu-offcanvas.scss";

interface MobileMenuOffcanvasProps {
  isOpen: boolean;
  toggle: () => void;
}

/**
 * MobileMenuOffcanvas — pure-Tailwind slide-out (Bootstrap `.offcanvas` removed
 * 2026-06-22). Panel mechanics (fixed, slide, backdrop) are Tailwind; the inner
 * content classes (mobile-menu-*, offcanvas-header/body) are styled by the
 * self-contained _mobile-menu-offcanvas.scss, so this survives Bootstrap removal.
 *
 * This is the WEB app's ONLY mobile (<768px) nav surface. The admin web modules
 * aren't built for phones, so on small screens we render ONLY field-app links
 * (FIELD_NAV_MODULES → /field/*); the desktop dock handles all admin modules.
 */
const MobileMenuOffcanvas: React.FC<MobileMenuOffcanvasProps> = ({
  isOpen,
  toggle,
}) => {
  const location = useLocation();

  const isActive = (route: string): boolean => {
    if (route === "/dashboard" || route === "/dashboard-modern") {
      return location.pathname === route;
    }
    return location.pathname.startsWith(route);
  };

  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") toggle();
    };
    document.addEventListener("keydown", onEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, toggle]);

  return createPortal(
    <>
      <div
        className="tw-fixed tw-inset-0 tw-z-[1040] tw-bg-black/50 tw-transition-opacity tw-duration-300"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? "auto" : "none" }}
        onClick={toggle}
        aria-hidden="true"
      />
      <div
        className="mobile-menu-offcanvas tw-fixed tw-inset-y-0 tw-left-0 tw-z-[1045] tw-flex tw-w-[300px] tw-max-w-[85%] tw-flex-col tw-bg-white tw-shadow-xl tw-transition-transform tw-duration-300 tw-ease-in-out"
        style={{ transform: isOpen ? "translateX(0)" : "translateX(-100%)" }}
        tabIndex={-1}
        aria-hidden={!isOpen}
      >
        <div className="offcanvas-header tw-flex tw-items-center tw-justify-between">
          <h5 className="offcanvas-title tw-mb-0">Field App</h5>
          <button
            type="button"
            className="tw-border-0 tw-bg-transparent tw-text-2xl tw-leading-none tw-text-slate-500 tw-cursor-pointer hover:tw-text-slate-700"
            aria-label="Close"
            onClick={toggle}
          >
            <i className="ri-close-line"></i>
          </button>
        </div>
        <div className="offcanvas-body tw-overflow-y-auto tw-p-0">
          <nav className="mobile-menu-nav">
            <ul className="mobile-menu-list">
              {FIELD_NAV_MODULES.map((module) => {
                const active = isActive(module.route);
                return (
                  <li key={module.id} className="mobile-menu-item">
                    <Link
                      to={module.route}
                      className={`mobile-menu-link ${active ? "active" : ""}`}
                      onClick={toggle}
                      aria-current={active ? "page" : undefined}
                    >
                      <i className={module.icon} aria-hidden="true"></i>
                      <span className="mobile-menu-label">{module.label}</span>
                      {active && (
                        <i className="ri-arrow-right-s-line mobile-menu-arrow"></i>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </div>
    </>,
    document.body
  );
};

export default MobileMenuOffcanvas;
